import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Users, Calendar, Target, TrendingUp, Archive, RotateCcw, AlertTriangle, ChevronRight, Check, X as XIcon, HelpCircle, ArrowRight, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";
import { format } from "date-fns";
import { fmt } from "@/lib/timezone";
import { useBrand } from "@/lib/brand";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const STATUS_COLORS = {
  active: "bg-green-100 text-green-700",
  interest_capture: "bg-amber-100 text-amber-700",
  closed: "bg-slate-100 text-slate-600",
  archived: "bg-slate-50 text-slate-400",
};

const CohortManagement = ({ batch, tenures, pricing, styles, focus, onClose }) => {
  const { default_timezone } = useBrand();
  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCohort, setSelectedCohort] = useState(null);
  const [cohortDetail, setCohortDetail] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // {type, cohortId, label}
  const interestSectionRef = React.useRef(null);

  const [createForm, setCreateForm] = useState({
    tenure_id: "",
    start_date: "",
    min_students: "3",
    low_class_warning_threshold: "4",
  });

  const fetchCohorts = useCallback(async () => {
    try {
      const res = await api.get(`/cohorts?batch_id=${batch.batch_id}`);
      setCohorts(res.data.cohorts);
    } catch {
      toast.error("Failed to load cohorts");
    } finally {
      setLoading(false);
    }
  }, [batch.batch_id]);

  useEffect(() => { fetchCohorts(); }, [fetchCohorts]);

  // When invoked with focus="interest", auto-open the most relevant cohort detail
  // (interest_capture > active > most recent) so the admin can record interest in 1 click.
  useEffect(() => {
    if (focus !== "interest" || cohorts.length === 0 || selectedCohort) return;
    const target =
      cohorts.find((c) => c.status === "interest_capture") ||
      cohorts.find((c) => c.status === "active") ||
      cohorts[0];
    if (target) fetchCohortDetail(target.cohort_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus, cohorts]);

  // Once detail is loaded with focus=interest, scroll to interest section
  useEffect(() => {
    if (focus === "interest" && cohortDetail && interestSectionRef.current) {
      setTimeout(() => {
        interestSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    }
  }, [focus, cohortDetail]);

  const fetchCohortDetail = async (cohortId) => {
    try {
      const res = await api.get(`/cohorts/${cohortId}`);
      setCohortDetail(res.data.cohort);
      setSelectedCohort(cohortId);
    } catch {
      toast.error("Failed to load cohort details");
    }
  };

  const createCohort = async () => {
    const { tenure_id, start_date, min_students, low_class_warning_threshold } = createForm;
    if (!tenure_id || !start_date) {
      toast.error("Tenure and start date are required");
      return;
    }
    const style = styles.find(s => s.style_id === batch.style_id);
    const price = pricing.find(p => p.style_id === batch.style_id && p.tenure_id === tenure_id);
    if (!price) {
      toast.error("No pricing found for this style and tenure combination");
      return;
    }
    try {
      await api.post("/cohorts", {
        batch_id: batch.batch_id,
        tenure_id,
        pricing_id: price.pricing_id,
        start_date: new Date(start_date).toISOString(),
        min_students: parseInt(min_students) || 1,
        low_class_warning_threshold: parseInt(low_class_warning_threshold) || 4,
      });
      toast.success("Cohort created");
      setShowCreateForm(false);
      setCreateForm({ tenure_id: "", start_date: "", min_students: "3", low_class_warning_threshold: "4" });
      fetchCohorts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create cohort");
    }
  };

  const executeAction = async () => {
    if (!confirmAction) return;
    const { type, cohortId } = confirmAction;
    try {
      if (type === "archive") {
        await api.put(`/cohorts/${cohortId}/archive`);
        toast.success("Cohort archived");
      } else if (type === "reopen") {
        await api.put(`/cohorts/${cohortId}/reopen`);
        toast.success("Cohort reopened");
      } else if (type === "start_interest") {
        await api.put(`/cohorts/${cohortId}/start-interest-capture`);
        toast.success("Interest capture started");
      } else if (type === "extend") {
        await api.post(`/cohorts/${cohortId}/extend`);
        toast.success("Cohort extended successfully");
      } else if (type === "undo_extension") {
        await api.post(`/cohorts/${cohortId}/undo-extension`);
        toast.success("Extension undone");
      }
      setConfirmAction(null);
      fetchCohorts();
      if (selectedCohort) fetchCohortDetail(selectedCohort);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Action failed");
      setConfirmAction(null);
    }
  };

  const updateInterest = async (studentId, status) => {
    if (!selectedCohort) return;
    try {
      const res = await api.put(`/cohorts/${selectedCohort}/interest`, {
        interest_updates: [{ student_id: studentId, status }],
      });
      // Update local state
      setCohortDetail(prev => ({
        ...prev,
        interest_data: res.data.interest_data,
        students: prev.students.map(s =>
          s.student_id === studentId ? { ...s, interest_status: status } : s
        ),
      }));
    } catch {
      toast.error("Failed to update interest");
    }
  };

  const activeCohort = cohorts.find(c => c.status === "active" || c.status === "interest_capture");
  const canCreateCohort = !activeCohort;

  if (loading) {
    return <div className="p-6 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" /></div>;
  }

  // Detail view of a selected cohort
  if (selectedCohort && cohortDetail) {
    const progress = cohortDetail.target_sessions > 0
      ? Math.round((cohortDetail.completed_sessions / cohortDetail.target_sessions) * 100)
      : 0;
    const remaining = cohortDetail.target_sessions - (cohortDetail.completed_sessions || 0);

    const yesCount = cohortDetail.students?.filter(s => s.interest_status === "yes").length || 0;
    const noCount = cohortDetail.students?.filter(s => s.interest_status === "no").length || 0;
    const maybeCount = cohortDetail.students?.filter(s => s.interest_status === "maybe").length || 0;
    const unmarked = cohortDetail.students?.filter(s => !s.interest_status).length || 0;

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => { setSelectedCohort(null); setCohortDetail(null); }} className="text-xs">
            &larr; Back to Cohorts
          </Button>
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_COLORS[cohortDetail.status]}`}>
            {cohortDetail.status.replace("_", " ").toUpperCase()}
          </span>
        </div>

        {/* Progress Card */}
        <div className="bg-white rounded-2xl p-4 border border-slate-100 space-y-3">
          <div className="flex justify-between items-center text-sm">
            <span className="text-slate-600">Session Progress</span>
            <span className="font-bold">{cohortDetail.completed_sessions || 0}/{cohortDetail.target_sessions}</span>
          </div>
          <div className="w-full bg-slate-100 rounded-full h-3">
            <div className="bg-violet-500 rounded-full h-3 transition-all" style={{ width: `${Math.min(progress, 100)}%` }} />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center text-xs">
            <div className="p-2 bg-slate-50 rounded-lg">
              <p className="text-slate-500">Start</p>
              <p className="font-bold text-slate-900" data-testid="cohort-detail-start">
                {cohortDetail.start_date ? fmt(cohortDetail.start_date, "dd MMM yy", default_timezone) : "—"}
              </p>
            </div>
            <div className="p-2 bg-slate-50 rounded-lg">
              <p className="text-slate-500">Remaining</p>
              <p className="font-bold text-slate-900">{remaining}</p>
            </div>
            <div className="p-2 bg-slate-50 rounded-lg">
              <p className="text-slate-500">Enrolled</p>
              <p className="font-bold text-slate-900">{cohortDetail.students?.length || 0}</p>
            </div>
            <div className="p-2 bg-slate-50 rounded-lg">
              <p className="text-slate-500">Est. End</p>
              <p className="font-bold text-slate-900">
                {cohortDetail.estimated_end_date ? fmt(cohortDetail.estimated_end_date, "dd MMM yy", default_timezone) : "—"}
              </p>
            </div>
          </div>
        </div>

        {/* Interest Capture Section */}
        {(cohortDetail.status === "interest_capture" || cohortDetail.status === "active") && (
          <div ref={interestSectionRef} className="bg-white rounded-2xl p-4 border border-slate-100 space-y-3">
            {cohortDetail.status === "active" && (
              <Button
                onClick={() => setConfirmAction({ type: "start_interest", cohortId: selectedCohort, label: "Start Interest Capture? This will move the cohort to interest capture phase." })}
                variant="outline"
                className="w-full rounded-xl text-sm text-amber-700 border-amber-200 hover:bg-amber-50"
                data-testid="start-interest-btn"
              >
                <Target className="w-4 h-4 mr-1" /> Start Interest Capture
              </Button>
            )}

            {cohortDetail.status === "interest_capture" && (
              <>
                <h4 className="text-sm font-semibold text-slate-800">Renewal Interest</h4>

                {/* Threshold indicator */}
                <div className={`p-3 rounded-xl text-xs ${yesCount >= cohortDetail.min_students ? "bg-green-50 border border-green-200 text-green-700" : "bg-red-50 border border-red-200 text-red-700"}`}>
                  <div className="flex justify-between items-center">
                    <span>{yesCount >= cohortDetail.min_students ? "Safe to Extend" : "Below Minimum Required Students"}</span>
                    <span className="font-bold">{yesCount}/{cohortDetail.min_students} min</span>
                  </div>
                  <div className="flex gap-3 mt-2">
                    <span className="text-green-600">Yes: {yesCount}</span>
                    <span className="text-red-600">No: {noCount}</span>
                    <span className="text-amber-600">Maybe: {maybeCount}</span>
                    {unmarked > 0 && <span className="text-slate-500">Unmarked: {unmarked}</span>}
                  </div>
                </div>

                {/* Student interest list */}
                <div className="space-y-2 max-h-[40vh] overflow-y-auto">
                  {(cohortDetail.students || []).map((student, i) => (
                    <div key={student.student_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl" data-testid={`interest-student-${i}`}>
                      <a
                        href={`/students?student_id=${student.student_id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          // Hard navigate so StudentManagement runs its deep-link useEffect cleanly
                          window.location.href = `/students?student_id=${student.student_id}`;
                        }}
                        className="min-w-0 flex-1 group"
                        data-testid={`interest-student-link-${i}`}
                        title="Open student profile"
                      >
                        <p className="text-sm font-medium text-slate-900 truncate group-hover:text-violet-700 group-hover:underline transition-colors">
                          {student.name}
                        </p>
                        <p className="text-xs text-slate-500 group-hover:text-violet-600 transition-colors">
                          {student.phone || "—"}
                        </p>
                      </a>
                      <div className="flex gap-1 ml-2">
                        <button
                          onClick={() => updateInterest(student.student_id, "yes")}
                          className={`p-1.5 rounded-lg text-xs font-medium transition-colors ${student.interest_status === "yes" ? "bg-green-500 text-white" : "bg-white border text-green-600 hover:bg-green-50"}`}
                          data-testid={`interest-yes-${i}`}
                        >
                          <Check className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => updateInterest(student.student_id, "maybe")}
                          className={`p-1.5 rounded-lg text-xs font-medium transition-colors ${student.interest_status === "maybe" ? "bg-amber-500 text-white" : "bg-white border text-amber-600 hover:bg-amber-50"}`}
                          data-testid={`interest-maybe-${i}`}
                        >
                          <HelpCircle className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => updateInterest(student.student_id, "no")}
                          className={`p-1.5 rounded-lg text-xs font-medium transition-colors ${student.interest_status === "no" ? "bg-red-500 text-white" : "bg-white border text-red-600 hover:bg-red-50"}`}
                          data-testid={`interest-no-${i}`}
                        >
                          <XIcon className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Extend button */}
                <Button
                  onClick={() => setConfirmAction({ type: "extend", cohortId: selectedCohort, label: `Extend cohort? A new cohort will be created for ${yesCount} interested student(s). Renewal invoices will be generated.` })}
                  className="w-full rounded-xl bg-violet-600 hover:bg-violet-700 text-sm"
                  disabled={yesCount === 0}
                  data-testid="extend-cohort-btn"
                >
                  <ArrowRight className="w-4 h-4 mr-1" /> Extend Cohort ({yesCount} students)
                </Button>
              </>
            )}
          </div>
        )}

        {/* Undo extension */}
        {cohortDetail.extended_to_cohort_id && (
          <Button
            onClick={() => setConfirmAction({ type: "undo_extension", cohortId: selectedCohort, label: "Undo extension? The new cohort and all its renewal invoices will be deleted." })}
            variant="outline"
            className="w-full rounded-xl text-xs text-red-600 border-red-200"
            data-testid="undo-extension-btn"
          >
            <Undo2 className="w-3 h-3 mr-1" /> Undo Extension
          </Button>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          {(cohortDetail.status === "active" || cohortDetail.status === "interest_capture") && (
            <Button
              onClick={() => setConfirmAction({ type: "archive", cohortId: selectedCohort, label: "Archive this cohort? It will be hidden from active views." })}
              variant="outline"
              size="sm"
              className="flex-1 rounded-xl text-xs"
            >
              <Archive className="w-3 h-3 mr-1" /> Archive
            </Button>
          )}
          {(cohortDetail.status === "closed" || cohortDetail.status === "archived") && (
            <Button
              onClick={() => setConfirmAction({ type: "reopen", cohortId: selectedCohort, label: "Reopen this cohort? It will become active again." })}
              variant="outline"
              size="sm"
              className="flex-1 rounded-xl text-xs text-green-600 border-green-200"
            >
              <RotateCcw className="w-3 h-3 mr-1" /> Reopen
            </Button>
          )}
        </div>

        {/* Confirmation Dialog */}
        <Dialog open={!!confirmAction} onOpenChange={() => setConfirmAction(null)}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle className="font-heading text-lg">Confirm Action</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-600">{confirmAction?.label}</p>
            <div className="flex gap-2 mt-4">
              <Button onClick={executeAction} className="flex-1 rounded-xl bg-violet-600 hover:bg-violet-700 text-xs">Confirm</Button>
              <Button onClick={() => setConfirmAction(null)} variant="outline" className="flex-1 rounded-xl text-xs">Cancel</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // List view
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg font-bold text-slate-900">Cohorts</h3>
        {canCreateCohort ? (
          <Button onClick={() => setShowCreateForm(true)} size="sm" className="rounded-full bg-violet-600 text-xs" data-testid="create-cohort-btn">
            + New Cohort
          </Button>
        ) : (
          <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-full">Active cohort exists</span>
        )}
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="bg-violet-50 border border-violet-200 rounded-2xl p-4 space-y-3">
          <h4 className="text-sm font-semibold text-violet-800">Create New Cohort</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Tenure *</Label>
              <select
                value={createForm.tenure_id}
                onChange={(e) => setCreateForm({ ...createForm, tenure_id: e.target.value })}
                className="w-full h-10 rounded-xl border px-3 bg-white text-sm"
                data-testid="cohort-tenure-select"
              >
                <option value="">Select Tenure</option>
                {tenures.map(t => (
                  <option key={t.tenure_id} value={t.tenure_id}>{t.name} ({t.duration_months}mo)</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Start Date *</Label>
              <Input
                type="date"
                value={createForm.start_date}
                onChange={(e) => setCreateForm({ ...createForm, start_date: e.target.value })}
                className="h-10 rounded-xl text-sm"
                data-testid="cohort-start-date"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Min. Students</Label>
              <Input
                type="number"
                min="1"
                value={createForm.min_students}
                onChange={(e) => setCreateForm({ ...createForm, min_students: e.target.value })}
                className="h-10 rounded-xl text-sm"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Low Class Warning</Label>
              <Input
                type="number"
                min="1"
                value={createForm.low_class_warning_threshold}
                onChange={(e) => setCreateForm({ ...createForm, low_class_warning_threshold: e.target.value })}
                className="h-10 rounded-xl text-sm"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={createCohort} size="sm" className="rounded-xl bg-violet-600 text-xs" data-testid="submit-cohort-btn">Create Cohort</Button>
            <Button onClick={() => setShowCreateForm(false)} size="sm" variant="outline" className="rounded-xl text-xs">Cancel</Button>
          </div>
        </motion.div>
      )}

      {/* Cohort List */}
      {cohorts.length === 0 ? (
        <div className="text-center py-8">
          <Target className="w-10 h-10 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">No cohorts yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {cohorts.map((cohort, i) => {
            const progress = cohort.target_sessions > 0
              ? Math.round(((cohort.completed_sessions || 0) / cohort.target_sessions) * 100)
              : 0;

            return (
              <motion.div
                key={cohort.cohort_id}
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                onClick={() => fetchCohortDetail(cohort.cohort_id)}
                className="bg-white rounded-2xl p-4 border border-slate-100 hover:border-violet-200 cursor-pointer transition-colors"
                data-testid={`cohort-card-${i}`}
              >
                <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_COLORS[cohort.status]}`}>
                      {cohort.status.replace("_", " ").toUpperCase()}
                    </span>
                    <span className="text-xs text-slate-500">{cohort.tenure_name} ({cohort.duration_months}mo)</span>
                    {cohort.status === "interest_capture" && (
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${cohort.threshold_met ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
                        {cohort.threshold_met ? "Threshold met" : "Below min"}
                      </span>
                    )}
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </div>

                {/* Detail grid: 2 cols on mobile, 4 on desktop. All key metadata at a glance. */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs mb-2">
                  <div className="p-2 bg-slate-50 rounded-lg">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wide">Start</p>
                    <p className="font-semibold text-slate-900 truncate">{cohort.start_date ? fmt(cohort.start_date, "dd MMM yy", default_timezone) : "—"}</p>
                  </div>
                  <div className="p-2 bg-slate-50 rounded-lg">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wide">Est. End</p>
                    <p className="font-semibold text-slate-900 truncate">{cohort.estimated_end_date ? fmt(cohort.estimated_end_date, "dd MMM yy", default_timezone) : "—"}</p>
                  </div>
                  <div className="p-2 bg-slate-50 rounded-lg">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wide">Sessions</p>
                    <p className="font-semibold text-slate-900">{cohort.completed_sessions || 0}/{cohort.target_sessions}</p>
                  </div>
                  <div className="p-2 bg-slate-50 rounded-lg">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wide">Enrolled</p>
                    <p className="font-semibold text-slate-900">{cohort.enrolled_count}</p>
                  </div>
                </div>

                {/* Second row: min threshold + interest counts + fee */}
                <div className="flex items-center justify-between gap-3 text-xs flex-wrap">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-slate-500">
                      Min: <span className="font-semibold text-slate-800">{cohort.min_students}</span>
                    </span>
                    <span className="text-slate-500">
                      Interested: <span className={`font-semibold ${cohort.threshold_met ? "text-green-600" : "text-amber-700"}`}>{cohort.interested ?? 0}</span>
                      {cohort.maybe ? <span className="text-slate-400"> · {cohort.maybe} maybe</span> : null}
                      {cohort.not_interested ? <span className="text-slate-400"> · {cohort.not_interested} no</span> : null}
                    </span>
                  </div>
                  <span className="font-semibold text-slate-700">₹{cohort.fee}</span>
                </div>

                <div className="w-full bg-slate-100 rounded-full h-1.5 mt-2">
                  <div className="bg-violet-500 rounded-full h-1.5 transition-all" style={{ width: `${Math.min(progress, 100)}%` }} />
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={!!confirmAction} onOpenChange={() => setConfirmAction(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">Confirm Action</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">{confirmAction?.label}</p>
          <div className="flex gap-2 mt-4">
            <Button onClick={executeAction} className="flex-1 rounded-xl bg-violet-600 hover:bg-violet-700 text-xs">Confirm</Button>
            <Button onClick={() => setConfirmAction(null)} variant="outline" className="flex-1 rounded-xl text-xs">Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CohortManagement;
